import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getUserProjects, deleteProject, duplicateProject, createProject, unpublishProject } from '../../services/projects';
import { createPage } from '../../services/pages';
import AppHeader from '../../components/AppHeader';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SiteCard from './components/SiteCard';
import EmptyState from './components/EmptyState';
import Icon from '../../components/AppIcon';


const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSiteName, setNewSiteName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Close modal on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showCreateModal) {
        setShowCreateModal(false);
        setNewSiteName('');
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showCreateModal]);

  // Helper: format relative time
  const formatRelativeTime = (date) => {
    if (!date) return null;
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'только что';
    if (minutes < 60) return `${minutes} мин. назад`;
    if (hours < 24) return `${hours} ч. назад`;
    if (days === 1) return 'вчера';
    if (days < 7) return `${days} дн. назад`;
    return date.toLocaleDateString('ru-RU');
  };

  // Helper: determine effective status
  const getEffectiveStatus = (project) => {
    if (project.status !== 'published') return 'draft';

    const updatedAt = project.updatedAt?.toDate?.();
    const publishedAt = project.publishedAt?.toDate?.();

    if (updatedAt && publishedAt && updatedAt > publishedAt) {
      return 'modified';
    }
    return 'published';
  };

  // Load projects from Firestore
  useEffect(() => {
    async function loadProjects() {
      if (!user) return;
      try {
        const projects = await getUserProjects(user.uid);
        const sitesData = projects.map(p => ({
          id: p.id,
          name: p.name,
          thumbnail: p.thumbnail || null,
          status: getEffectiveStatus(p),
          subdomain: p.subdomain || null,
          customDomain: p.customDomain || null,
          updatedAt: p.updatedAt?.toDate?.(),
          updatedAtFormatted: formatRelativeTime(p.updatedAt?.toDate?.())
        }));
        setSites(sitesData);
      } catch (error) {
        console.error('Error loading projects:', error);
      } finally {
        setLoading(false);
      }
    }
    loadProjects();
  }, [user]);

  const filteredSites = useMemo(() => {
    if (!searchQuery) return sites;
    return sites.filter((site) =>
      site?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
    );
  }, [sites, searchQuery]);

  const handleDeleteSite = async (siteId) => {
    try {
      await deleteProject(siteId);
      setSites((prevSites) => prevSites?.filter((site) => site?.id !== siteId));
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const handleUnpublishSite = async (siteId) => {
    await unpublishProject(siteId);
    setSites((prevSites) => prevSites.map((site) =>
      site.id === siteId
        ? { ...site, status: 'draft', updatedAt: new Date(), updatedAtFormatted: 'только что' }
        : site
    ));
  };

  const handleDuplicateSite = async (siteId) => {
    try {
      const newProject = await duplicateProject(siteId, user.uid);
      const newSite = {
        id: newProject.id,
        name: newProject.name,
        thumbnail: null,
        status: 'draft',
        subdomain: null,
        customDomain: null,
        updatedAt: new Date(),
        updatedAtFormatted: 'только что'
      };
      setSites((prevSites) => [newSite, ...prevSites]);
    } catch (error) {
      console.error('Error duplicating project:', error);
    }
  };

  const handleCreateSite = async () => {
    if (isCreating || !newSiteName.trim()) return;
    setIsCreating(true);
    try {
      const project = await createProject(user.uid, {
        name: newSiteName.trim(),
        template: 'blank'
      });
      await createPage(project.id, {
        name: 'Главная',
        slug: 'index',
        sections: [],
        order: 0
      });
      navigate(`/projects/${project.id}/editor`);
    } catch (error) {
      console.error('Error creating project:', error);
      setIsCreating(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader
        user={{ name: user?.displayName || 'Пользователь', email: user?.email }}
        onLogout={handleLogout}
      />

      <main className="pt-16 pb-8 px-4 md:px-6 lg:px-8">
        <div className="max-w-screen-2xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-foreground">
                Мои сайты {sites.length > 0 && <span className="text-text-secondary font-normal">({sites.length})</span>}
              </h1>
            </div>
            <Button
              variant="default"
              size="default"
              iconName="Plus"
              iconPosition="left"
              onClick={() => setShowCreateModal(true)}
            >
              Создать сайт
            </Button>
          </div>

          {/* Search - only if has sites */}
          {sites.length > 0 && (
            <div className="mb-6">
              <div className="relative max-w-md">
                <Icon
                  name="Search"
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none"
                />
                <input
                  type="text"
                  placeholder="Поиск по названию..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 bg-card border border-border rounded-xl text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted transition-colors"
                    aria-label="Очистить поиск"
                  >
                    <Icon name="X" size={16} className="text-text-secondary" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Content */}
          {filteredSites.length === 0 && sites.length > 0 ? (
            <div className="bg-card rounded-2xl border border-border p-8 md:p-12 text-center">
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
                  <Icon name="Search" size={28} className="text-text-secondary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Ничего не найдено
                </h3>
                <p className="text-text-secondary text-sm">
                  Попробуйте изменить поисковый запрос
                </p>
              </div>
            </div>
          ) : sites.length === 0 ? (
            <EmptyState onCreateClick={() => setShowCreateModal(true)} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
              {filteredSites.map((site) => (
                <SiteCard
                  key={site.id}
                  site={site}
                  onDelete={handleDeleteSite}
                  onDuplicate={handleDuplicateSite}
                  onUnpublish={handleUnpublishSite}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Create Site Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4"
          onClick={() => {
            setShowCreateModal(false);
            setNewSiteName('');
          }}
        >
          <div
            className="bg-card rounded-2xl shadow-elevation-4 border border-border p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-foreground">Новый сайт</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewSiteName('');
                }}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground mb-2">
                Название сайта
              </label>
              <Input
                value={newSiteName}
                onChange={(e) => setNewSiteName(e.target.value)}
                placeholder="Мой сайт"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newSiteName.trim()) {
                    handleCreateSite();
                  }
                }}
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateModal(false);
                  setNewSiteName('');
                }}
                fullWidth
              >
                Отмена
              </Button>
              <Button
                variant="default"
                onClick={handleCreateSite}
                disabled={!newSiteName.trim() || isCreating}
                loading={isCreating}
                fullWidth
              >
                Создать
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

};

export default Dashboard;